import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Container, Paper, Typography, Button, Divider, 
    Grid, Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, IconButton, CircularProgress,
    Stack, Chip, Avatar
} from '@mui/material';
import { 
    ArrowBack, Print, Download, ReceiptLong, 
    CheckCircle, Business, Person, CalendarMonth,
    AccountBalance, AccountBalanceWallet
} from '@mui/icons-material';
import styled from 'styled-components';
import { getEmployeePaymentHistory } from '../../../redux/salaryRelated/salaryHandle';
import { getNepaliMonthOptions } from '../../../utils/customDate';

const ReceiptContainer = styled(Paper)`
    background: white;
    padding: 60px;
    border-radius: 0;
    box-shadow: none;
    position: relative;
    overflow: hidden;
    
    @media print {
        padding: 20px;
        box-shadow: none;
    }
`;

const Watermark = styled(Typography)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-45deg);
    font-size: 10rem;
    color: rgba(0, 0, 0, 0.03);
    font-weight: 900;
    pointer-events: none;
    white-space: nowrap;
    text-transform: uppercase;
`;

const HeaderSection = styled(Box)`
    border-bottom: 2px solid #1a1a2e;
    padding-bottom: 20px;
    margin-bottom: 40px;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const SalaryReceipt = () => {
    const { id, month, year } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { currentUser } = useSelector(state => state.user);
    const { paymentHistory, loading } = useSelector(state => state.salary);
    
    const [receiptData, setReceiptData] = useState(null);

    useEffect(() => {
        if (id && currentUser?._id) {
            // We need to know if it's a teacher or staff. 
            // For now let's try teacher, then staff if not found, 
            // or we could have passed it in the URL.
            // Let's assume we can fetch history and filter.
            dispatch(getEmployeePaymentHistory(currentUser._id, 'teacher', id));
        }
    }, [id, currentUser, dispatch]);

    useEffect(() => {
        if (paymentHistory && paymentHistory.length > 0) {
            const specificPayment = paymentHistory.find(p => p.month === month && p.year === parseInt(year));
            if (specificPayment) {
                setReceiptData(specificPayment);
            }
        }
    }, [paymentHistory, month, year]);

    const handlePrint = () => {
        window.print();
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-NP', {
            style: 'currency',
            currency: 'NPR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    if (loading && !receiptData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!receiptData) {
        return (
            <Container maxWidth="md" sx={{ py: 10, textAlign: 'center' }}>
                <Typography variant="h5" color="text.secondary">Receipt not found for this period.</Typography>
                <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>Go Back</Button>
            </Container>
        );
    }

    return (
        <Box sx={{ bgcolor: '#f1f5f9', minHeight: '100vh', py: 6 }}>
            <Container maxWidth="md">
                {/* Actions Toolbar */}
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', '@media print': { display: 'none' } }}>
                    <Button 
                        startIcon={<ArrowBack />} 
                        onClick={() => navigate(-1)}
                        sx={{ color: '#1a1a2e', fontWeight: 600 }}
                    >
                        Back to Registry
                    </Button>
                    <Stack direction="row" spacing={2}>
                        <Button 
                            variant="outlined" 
                            startIcon={<Download />}
                            sx={{ borderRadius: '10px' }}
                        >
                            Download PDF
                        </Button>
                        <Button 
                            variant="contained" 
                            startIcon={<Print />}
                            onClick={handlePrint}
                            sx={{ bgcolor: '#1a1a2e', borderRadius: '10px', px: 4 }}
                        >
                            Print Receipt
                        </Button>
                    </Stack>
                </Box>

                {/* The Actual Receipt */}
                <ReceiptContainer elevation={0}>
                    <Watermark variant="h1">OFFICIAL</Watermark>
                    
                    <HeaderSection>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Business sx={{ color: '#1a1a2e', fontSize: 32 }} />
                                <Typography variant="h4" sx={{ fontWeight: 900, color: '#1a1a2e' }}>
                                    {currentUser?.schoolName || 'INSTITUTION NAME'}
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                Institutional Payroll System • Official Document
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a1a2e' }}>PAYSLIP</Typography>
                            <Typography variant="body2" color="text.secondary">#{receiptData._id.substring(0, 8).toUpperCase()}</Typography>
                        </Box>
                    </HeaderSection>

                    <Grid container spacing={4} sx={{ mb: 6 }}>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 700, mb: 1, textTransform: 'uppercase' }}>
                                Employee Details
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: '#1a1a2e', width: 50, height: 50 }}>
                                    <Person />
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{receiptData.employee?.name || 'Employee'}</Typography>
                                    <Typography variant="body2" color="text.secondary">{receiptData.position || 'Staff'}</Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sx={{ textAlign: 'right' }}>
                            <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 700, mb: 1, textTransform: 'uppercase' }}>
                                Payment Period
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                                <CalendarMonth color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                    {getNepaliMonthOptions().find(m => m.value === receiptData.month)?.label || receiptData.month} {receiptData.year}
                                </Typography>
                            </Box>
                            <Chip 
                                icon={<CheckCircle />} 
                                label="Status: Paid" 
                                color="success" 
                                size="small" 
                                sx={{ mt: 1, fontWeight: 700, borderRadius: '6px' }} 
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ mb: 4 }} />

                    <Grid container spacing={6}>
                        {/* Earnings */}
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccountBalance color="success" /> Earnings
                            </Typography>
                            <Table size="small">
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{ border: 'none', py: 1.5 }}>Basic Salary</TableCell>
                                        <TableCell align="right" sx={{ border: 'none', fontWeight: 600 }}>{formatCurrency(receiptData.baseSalary)}</TableCell>
                                    </TableRow>
                                    {Object.entries(receiptData.allowances || {}).map(([key, val]) => val > 0 && (
                                        <TableRow key={key}>
                                            <TableCell sx={{ border: 'none', py: 1.5, textTransform: 'capitalize' }}>{key.replace(/([A-Z])/g, ' $1')}</TableCell>
                                            <TableCell align="right" sx={{ border: 'none', fontWeight: 600 }}>{formatCurrency(val)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Grid>

                        {/* Deductions */}
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccountBalanceWallet color="error" /> Deductions
                            </Typography>
                            <Table size="small">
                                <TableBody>
                                    {Object.entries(receiptData.deductions || {}).map(([key, val]) => val > 0 && (
                                        <TableRow key={key}>
                                            <TableCell sx={{ border: 'none', py: 1.5, textTransform: 'capitalize' }}>{key.replace(/([A-Z])/g, ' $1')}</TableCell>
                                            <TableCell align="right" sx={{ border: 'none', fontWeight: 600, color: '#ef4444' }}>-{formatCurrency(val)}</TableCell>
                                        </TableRow>
                                    ))}
                                    {Object.values(receiptData.deductions || {}).every(v => v === 0) && (
                                        <TableRow>
                                            <TableCell sx={{ border: 'none', py: 1.5, color: 'text.secondary' }}>No deductions</TableCell>
                                            <TableCell align="right" sx={{ border: 'none' }}>-</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 6, p: 4, bgcolor: '#1a1a2e', color: 'white', borderRadius: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="subtitle1" sx={{ opacity: 0.8, fontWeight: 600 }}>Net Payable Amount</Typography>
                            <Typography variant="body2" sx={{ opacity: 0.6 }}>Settled via {receiptData.paymentMethod || 'Cash'}</Typography>
                        </Box>
                        <Typography variant="h3" sx={{ fontWeight: 900 }}>
                            {formatCurrency(receiptData.netSalary)}
                        </Typography>
                    </Box>

                    <Box sx={{ mt: 8, display: 'flex', justifyContent: 'space-between' }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Box sx={{ width: 180, borderBottom: '1px solid #cbd5e1', mb: 1, pt: 6 }} />
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>EMPLOYEE SIGNATURE</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                            <Box sx={{ width: 180, borderBottom: '1px solid #cbd5e1', mb: 1, pt: 6 }} />
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>AUTHORIZED SIGNATORY</Typography>
                        </Box>
                    </Box>

                    <Box sx={{ mt: 10, textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                            This is a computer-generated document and does not require a physical signature if verified online.
                        </Typography>
                    </Box>
                </ReceiptContainer>
            </Container>
        </Box>
    );
};

export default SalaryReceipt;
